import { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "primeicons/primeicons.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { BiSearch } from "react-icons/bi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import CourseService from "../../../services/CourseService";

const ReleaseIncidentsMapping = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { state } = useLocation();
  const authContext = useContext(AuthContext);

  // State variables
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [courseIncidents, setCourseIncidents] = useState([]);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  const [selectAllLeft, setSelectAllLeft] = useState(false);
  const [selectAllRight, setSelectAllRight] = useState(false);
  const [assignedIncidents, setAssignedIncidents] = useState([]);
  const [removedIncidents, setRemovedIncidents] = useState([]);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [initialAssignments, setInitialAssignments] = useState([]);
  const [hasViewPermission, setHasViewPermission] = useState(false);
  const submitControllerRef = useRef(null);

  const courseId = params.id || state?.id;
  const getFormattedCourseName = (raw) => {
    const formatted = raw?.replace(/-/g, " ");
    return formatted?.replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const courseName = getFormattedCourseName(params?.courseName);

  const courseAccess = useMemo(
    () => authContext.getPermissions("courses"),
    [authContext],
  );
  const showAdd = useMemo(
    () => courseAccess.filter((f) => f.access.includes(2)).length > 0,
    [courseAccess],
  );
  const showEdit = useMemo(
    () => courseAccess.filter((f) => f.access.includes(3)).length > 0,
    [courseAccess],
  );

  useEffect(() => {
    const hasPermission =
      courseAccess.filter((f) => f.access.includes(1)).length > 0;
    setHasViewPermission(hasPermission);
    if (!hasPermission) {
      navigate("/");
    }
  }, [courseAccess, navigate]);

  const loadCourseIncidents = useCallback(async (signal) => {
    if (!courseId || !hasViewPermission) return;

    setIsInitialLoading(true);
    try {
      const data = await CourseService.GetReleaseIncidents(
        courseId,
        "",
        authContext,
        signal 
      );
      if (data.isSuccess) {
        const processedMapped =
          data.data?.releasedIncidents?.map((incident) => ({
            ...incident,
              categoryName: incident.title,
            selected: false,
          })) || [];
        const uniqueAll = Array.from(
          new Map(
            (data.data?.allIncidents || []).map((i) => [i.value, i]),
          ).values(),
        );

        const processedAll = uniqueAll.map((incident) => ({
          ...incident,
            categoryName: incident.title,
          selected: false,
        }));

        setCourseIncidents(processedMapped);
        setInitialAssignments(processedMapped.map((i) => ({ ...i })));
        if (processedMapped.length > 0) {
          setIncidents(
            processedAll.filter(
              (f) => !processedMapped.some((d) => d.value === f.value),
            ),
          );
        } else {
          setIncidents(processedAll);
        }
      } else {
        AlertService.error(data.errorMessage);
      }
      } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error("Failed to load incidents");
    } finally {
      if (!signal?.aborted) setIsInitialLoading(false); 
    }
  }, [courseId, authContext, hasViewPermission]);


  useEffect(() => {
    const controller = new AbortController(); 
    if (courseId && hasViewPermission) {
      loadCourseIncidents(controller.signal);
    }
     return () => controller.abort();
  }, [courseId, loadCourseIncidents, hasViewPermission]);

  useEffect(() => {
    const currentValues = courseIncidents.map((i) => i.value);
    const newlyAssigned = courseIncidents.filter(
      (incident) =>
        !initialAssignments.some((init) => init.value === incident.value),
    );
    const removed = initialAssignments.filter(
      (init) => !currentValues.includes(init.value),
    );

    setAssignedIncidents(newlyAssigned);
    setRemovedIncidents(removed);
  }, [courseIncidents, initialAssignments]);

  const filteredLeftIncidents = useMemo(() => {
    return incidents.filter((incident) =>
      incident.label.toLowerCase().includes(searchLeft.toLowerCase()),
    );
  }, [incidents, searchLeft]);

  const groupedLeftIncidents = useMemo(() => {
  const grouped = {};

  filteredLeftIncidents.forEach((incident) => {
    const category = incident.categoryName || "Others";

    if (!grouped[category]) {
      grouped[category] = [];
    }

    grouped[category].push(incident);
  });

  return Object.entries(grouped).map(([categoryName, incidents]) => ({
    categoryName,
    incidents
  }));
}, [filteredLeftIncidents]);

  const filteredRightIncidents = useMemo(() => {
    return courseIncidents.filter((incident) =>
      incident.label.toLowerCase().includes(searchRight.toLowerCase()),
    );
  }, [courseIncidents, searchRight]);



    useEffect(() => {
  if (groupedLeftIncidents.length > 0) {
    setSelectAllLeft(
      groupedLeftIncidents.every(cat =>
        cat.incidents.every(i => i.selected)
      )
    );
  } else {
    setSelectAllLeft(false);
  }
}, [groupedLeftIncidents]);

const groupedRightIncidents = useMemo(() => {
  const grouped = {};

  filteredRightIncidents.forEach((incident) => {
    const category = incident.categoryName  || "Others";

    if (!grouped[category]) {
      grouped[category] = [];
    }

    grouped[category].push(incident);
  });

  return Object.entries(grouped).map(([categoryName, incidents]) => ({
    categoryName,
    incidents
  }));
}, [filteredRightIncidents]);

    useEffect(() => {
  if (groupedRightIncidents.length > 0) {
    setSelectAllRight(
      groupedRightIncidents.every(cat =>
        cat.incidents.every(i => i.selected)
      )
    );
  } else {
    setSelectAllRight(false);
  }
}, [groupedRightIncidents]);

  const toggleIncidentSelection = useCallback((value, isRightPanel = false) => {
    const setter = isRightPanel ? setCourseIncidents : setIncidents;
    setter((prev) =>
      prev.map((incident) =>
        incident.value === value
          ? { ...incident, selected: !incident.selected }
          : incident,
      ),
    );
  }, []);

  const toggleSelectAll = useCallback(
    (isRightPanel = false) => {
      const setter = isRightPanel ? setCourseIncidents : setIncidents;
      const filtered = isRightPanel
        ? filteredRightIncidents
        : filteredLeftIncidents;
      const allSelected = isRightPanel ? selectAllRight : selectAllLeft;

      const valuesToUpdate = filtered.map((i) => i.value);

      setter((prev) =>
        prev.map((incident) =>
          valuesToUpdate.includes(incident.value)
            ? { ...incident, selected: !allSelected }
            : incident,
        ),
      );
    },
    [
      filteredLeftIncidents,
      filteredRightIncidents,
      selectAllLeft,
      selectAllRight,
    ],
  );

      const toggleCategorySelection = (categoryName) => {
  setIncidents(prev =>
    prev.map(incident => {
      if ((incident.categoryName || "Others") !== categoryName) return incident;

      return {
        ...incident,
        selected: !groupedLeftIncidents
          .find(c => c.categoryName === categoryName)
          .incidents.every(i => i.selected)
      };
    })
  );
};

const toggleRightCategorySelection = (categoryName) => {
  setCourseIncidents(prev =>
    prev.map(incident => {
      if ((incident.categoryName || "Others") !== categoryName) return incident;

      return {
        ...incident,
        selected: !groupedRightIncidents
          .find(c => c.categoryName === categoryName)
          .incidents.every(i => i.selected)
      };
    })
  );
};

  const moveIncidents = useCallback(
    (direction) => {
      if (direction === "right") {
        const selectedIncidents = incidents.filter((i) => i.selected);

        setCourseIncidents((prev) => [
          ...prev,
          ...selectedIncidents.map((i) => ({
            ...i,
            selected: false,
          })),
        ]);

        setIncidents((prev) => prev.filter((i) => !i.selected));
      } else {
        const selectedIncidents = courseIncidents.filter((i) => i.selected);

        setIncidents((prev) => [
          ...prev,
          ...selectedIncidents.map((i) => ({ ...i, selected: false })),
        ]);

        setCourseIncidents((prev) => prev.filter((i) => !i.selected));
      }
    },
    [incidents, courseIncidents],
  );

  const hasChanges = useMemo(() => {
    return assignedIncidents.length > 0 || removedIncidents.length > 0;
  }, [assignedIncidents, removedIncidents]);

  const handlePreviewClick = (e) => {
    e.preventDefault();
    if (!hasChanges) {
      AlertService.warning("No changes to preview");
      return;
    }
    setShowChangesModal(true);
  };

const groupedPreviewIncidents = useMemo(() => {
  const grouped = {};

  courseIncidents.forEach((incident) => {
    const category = incident.categoryName || "Others";

    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(incident);
  });

  return Object.entries(grouped).map(([categoryName, incidents]) => ({
    categoryName,
    incidents
  }));
}, [courseIncidents]);

// Component unmount
useEffect(() => {
    return () => {
      submitControllerRef.current?.abort(); 
    };
  }, []);

const confirmSubmit = useCallback(async () => {
    if (submitControllerRef.current) {
      submitControllerRef.current.abort(); 
    }
    const controller = new AbortController();
    submitControllerRef.current = controller;

    setIsSaving(true);
    setShowChangesModal(false);

    const mergedAll = Array.from(
      new Map(
        [...incidents, ...courseIncidents].map((i) => [i.value, i]),
      ).values(),
    );

    try {
      const payload = {
        courseId: Number(courseId),
        incidents: courseIncidents.map((i) => Number(i.value)),
        releasedIncidents: courseIncidents,
        allIncidents: mergedAll,
      };

      const data = await CourseService.SaveReleaseIncidents(
        payload,
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (data.isSuccess) {
        AlertService.success("Incident assignments updated successfully");
        loadCourseIncidents();
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error("Failed to save incident assignments");
    } finally {
      if (!controller.signal.aborted) setIsSaving(false);
    }
  }, [courseId, courseIncidents, incidents, authContext, loadCourseIncidents]);

  const handleCancel = () => {
    navigate(-1);
  };

  const renderSkeletonRows = useCallback((count) => {
    return Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom"
      >
        <div className="me-2">
          <Skeleton shape="circle" size="20px" />
        </div>
        <Skeleton width="70%" height="1.5rem" />
      </div>
    ));
  }, []);

  const modalFooter = (
    <div className="pt-2">
      <button
        className="btn btn-outline-secondary px-5 py-2 me-4"
        onClick={() => setShowChangesModal(false)}
      >
        Back to Edit
      </button>
      <button
        className="btn btn-primary px-5 py-2"
        onClick={async () => {
          await confirmSubmit();
          setShowChangesModal(false);
        }}
      >
        Submit
      </button>
    </div>
  );

  return (
    <div className="container-fluid custom-container mt-4 mb-3">
      <div className="row g-1">
        <div
          className="col-12 col-md border rounded p-0 card"
          style={{ minWidth: "300px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {isInitialLoading ? (
                <Skeleton width="24px" height="24px" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllLeft}
                  onChange={() => toggleSelectAll(false)}
                  disabled={filteredLeftIncidents.length === 0}
                  className="me-2"
                />
              )}
              <span className="fw-bold">
                {isInitialLoading ? (
                  <Skeleton width="120px" />
                ) : (
                  "Master Repository"
                )}
              </span>
            </div>
            <div className="position-relative">
              {isInitialLoading ? (
                <Skeleton width="100%" height="38px" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchLeft}
                    onChange={(e) => setSearchLeft(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>
          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "670px", overflowY: "auto" }}
          >
            {isInitialLoading || isSaving ? (
              renderSkeletonRows(16)
            ) : filteredLeftIncidents.length > 0 ? (
             groupedLeftIncidents.map(category => {
  const allSelected = category.incidents.every(i => i.selected);

  return (
    <div key={category.categoryName} className="mb-0" >

      {/* CATEGORY HEADER */}
    <div className="d-flex align-items-center pb-3 pt-3 ps-3 border-bottom" style={{ cursor: 'pointer' }}>
        <Checkbox
          checked={allSelected}
          onChange={() => toggleCategorySelection(category.categoryName)}

        />
        <span className="ms-2 fw-bold">{category.categoryName}</span>
      </div>

      {/* INCIDENTS */}
      {category.incidents.map(incident => (
        <div
          key={incident.value}
              className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom position-relative"
                style={{ minHeight: '56px' }}
        >
          <Checkbox
            checked={incident.selected}
            onChange={() => toggleIncidentSelection(incident.value, false)}
              className="ms-4 flex-shrink-0"
          />
          <span className="ms-3 text-break hyphens-auto flex-grow-1"   
           style={{ 
        wordBreak: 'break-word', 
        lineHeight: '1.4',
        overflowWrap: 'break-word'
      }}>{incident.label}</span>
        </div>
      ))}
    </div>
  );
})
            ) : (
              <div className="text-center p-5 text-muted">
                No incidents available
              </div>
            )}
          </div>
        </div>

        {/* Middle buttons */}
        <div className="col-12 col-md-2 col-lg-1 d-flex flex-md-column justify-content-center align-items-center gap-3">
          {showAdd && (
            <Button
              icon="pi pi-arrow-right"
              className="btn border-0 fw-bold"
              style={{ backgroundColor: "#9CF", minWidth: "60px" }}
              onClick={() => moveIncidents("right")}
              disabled={
                isInitialLoading ||
                isSaving ||
                !incidents.some((i) => i.selected)
              }
            />
          )}
          {showEdit && (
            <Button
              icon="pi pi-arrow-left"
              className="btn border-0 fw-bold"
              style={{ backgroundColor: "#9CF", minWidth: "60px" }}
              onClick={() => moveIncidents("left")}
              disabled={
                isInitialLoading ||
                isSaving ||
                !courseIncidents.some((i) => i.selected)
              }
            />
          )}
        </div>

        {/* Right Panel */}
        <div
          className="col-12 col-md border rounded card p-0"
          style={{ minWidth: "300px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {isInitialLoading ? (
                <Skeleton width="24px" height="24px" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllRight}
                  onChange={() => toggleSelectAll(true)}
                  disabled={filteredRightIncidents.length === 0}
                  className="me-2"
                />
              )}
              <span className="fw-bold">
                {isInitialLoading ? (
                  <Skeleton width="130px" />
                ) : (
                  "Selected Repository"
                )}
              </span>
            </div>
            <div className="position-relative">
              {isInitialLoading ? (
                <Skeleton width="100%" height="38px" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchRight}
                    onChange={(e) => setSearchRight(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>
          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "670px", overflowY: "auto" }}
          >
            {isInitialLoading || isSaving ? (
              renderSkeletonRows(16)
            ) : filteredRightIncidents.length > 0 ? (
             groupedRightIncidents.map(category => {
               const allSelected = category.incidents.every(i => i.selected);
             
               return (
                 <div key={category.categoryName} className="mb-0">
             
                   {/* CATEGORY HEADER */}
                  <div className="d-flex align-items-center pb-3 pt-3 ps-3 border-bottom" style={{ cursor: 'pointer' }}>
                     <Checkbox
                       checked={allSelected}
                       onChange={() => toggleRightCategorySelection(category.categoryName)}
             
                     />
                     <span className="ms-2 fw-bold">{category.categoryName}</span>
                   </div>
             
                   {/* INCIDENTS */}
                   {category.incidents.map(incident => (
                     <div
                       key={incident.value}
                       className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom  position-relative"
                       style={{ minHeight: '56px' }}
                     >
                       <div className="d-flex align-items-center   flex-grow-1 pe-5">
                         <Checkbox
                           checked={incident.selected}
                           onChange={() => toggleIncidentSelection(incident.value, true)}
                           className="ms-4 flex-shrink-0"
                         />
                                         <span className="ms-3 text-break hyphens-auto" style={{ wordBreak: 'break-word', lineHeight: '1.4' , overflowWrap: 'break-word' }}>
                                           {incident.label}</span>
                       </div>
                     </div>
                   ))}
                 </div>
               );
             })
            ) : (
              <div className="text-center p-5 text-muted">
                No incidents assigned
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 d-flex justify-content-end gap-4">
          <Button
            label="Cancel"
            className="btn btn-outline px-5"
            onClick={handleCancel}
          />
          <Button
            className="btn btn-primary px-5 py-2"
            onClick={handlePreviewClick}
            disabled={isInitialLoading || isSaving || !hasChanges}
          >
            Preview
          </Button>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog
        header={<div className="w-100 text-center">Preview</div>}
        visible={showChangesModal}
        style={{ width: "40vw", height: "85vh" }}
        footer={modalFooter}
        onHide={() => setShowChangesModal(false)}
      >

      <div className="pb-3 border-bottom">
    <h5 className="mb-0">
      <b>Course Name</b>: {courseName}
    </h5>
  </div>

  {/* SCROLL AREA */}
  <div
    style={{
      maxHeight: "60vh",
      overflowY: "auto",
      paddingTop: "1rem"
    }}
  >
          {assignedIncidents.length > 0 && (
            <div className="mb-3">
              <h6 className="fw-bold mb-0">Added to Selected Repository</h6>
              <ul className="list-group">
                {assignedIncidents.map((i) => (
                  <li key={i.value} className="list-group-item text-primary">
                    {i.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {removedIncidents.length > 0 && (
            <div className="mb-3">
              <h6 className="fw-bold mb-0">Removed to Master Repository</h6>
              <ul className="list-group">
                {removedIncidents.map((i) => (
                  <li key={i.value} className="list-group-item text-primary">
                    {i.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h6 className="fw-bold mb-3 ">Final Selected Repository</h6>
            {groupedPreviewIncidents.map(category => (
  <div key={category.categoryName} className="mb-2">
    <h6 className="fw-semibold mb-0">{category.categoryName}</h6>

    <ul className="list-group">
      {category.incidents.map((i, index) => (
        <li key={i.value} className="list-group-item text-primary">
          {index + 1}. {i.label}
        </li>
      ))}
    </ul>
  </div>
))}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ReleaseIncidentsMapping;
